import clsx from 'clsx'
import React, { CSSProperties, useEffect, useRef, useState } from 'react'
import CartButtonLink from 'shared-components/buttons/CartButtonLink'
import { ResponsiveContext } from 'shared-components/contexts/ResponsiveContext'
import Linked from 'shared-components/service/Linked'
import BestCard from 'shared-components/ui/BestCard'
import BestFeatures from 'shared-components/ui/BestFeatures'
import BestHeader from 'shared-components/ui/BestHeader'
import ScrollHandleBar from 'shared-components/ui/ScrollHandleBar'
import SplitterLine from 'shared-components/ui/SplitterLine'
import { BasicStyledComponent, DSBest, UIRatingVariant } from 'shared-definitions/types'
import ArrowBird from 'shared-svg/ArrowRightBird.svg'
import styles from './BestList.module.css'
import { AppEventsContext } from '../contexts/AppEventsContext'

interface BestListProps extends BasicStyledComponent {
  bestList: readonly DSBest[]
  cardScoreVariant?: UIRatingVariant
}

const BestList: React.VFC<BestListProps> = ({ className, bestList, cardScoreVariant }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [defaultColumns, setDefaultColumns] = useState(5)
  const slider = useRef<HTMLDivElement>(null)
  const { ready, lg, xxl } = ResponsiveContext.useContainer()
  const { appEvent } = AppEventsContext.useContainer()

  useEffect(() => {
    if (!ready) {
      return
    }

    let newDef = 5
    if (xxl) {
      newDef = 5
    } else if (lg) {
      newDef = 3
    }

    setDefaultColumns(newDef)
    setActiveIndex(s => Math.max(Math.min(bestList.length - newDef, s), 0))
  }, [ready, lg, xxl, bestList])

  const scrollDisabled = bestList.length <= defaultColumns
  return (
    <div
      className={styles.controls}
      style={
        {
          '--default-columns': defaultColumns,
          '--active-index': activeIndex,
          '--columns': bestList.length,
        } as CSSProperties
      }
    >
      <ScrollHandleBar
        className={clsx(styles.handlebar, { [styles.hidden]: scrollDisabled })}
        current={activeIndex}
        all={bestList.length}
        visible={defaultColumns}
      />
      <button
        type="button"
        className={clsx(styles.scrollBack, {
          [styles.disabled]: activeIndex === 0,
          [styles.hidden]: scrollDisabled,
        })}
        onClick={() => setActiveIndex(s => Math.max(0, s - 1))}
      >
        <ArrowBird className={styles.iconBack} />
      </button>
      <button
        type="button"
        className={clsx(styles.scrollForward, {
          [styles.disabled]: activeIndex === bestList.length - defaultColumns,
          [styles.hidden]: scrollDisabled,
        })}
        onClick={() => setActiveIndex(s => Math.min(bestList.length - defaultColumns, s + 1))}
      >
        <ArrowBird className={styles.iconRight} />
      </button>
      <div className={styles.scroller} ref={slider}>
        <div className={clsx(styles.container, className)}>
          {bestList.map((item, index) => (
            <React.Fragment key={index}>
              <BestHeader
                className={clsx(styles.headerOne, { [styles.featuredHeader]: item.isFeatured })}
                item={item}
              />
              <BestCard scoreVariant={cardScoreVariant} item={item} className={styles.card} />
              {item.shopLink ? (
                <CartButtonLink
                  onClick={() => {
                    appEvent({
                      event: 'widget-interaction',
                      widgetType: 'Clearbuy Best List Topper',
                      label: 'Check price',
                      url: item.shopLink?.href ?? '',
                    })
                  }}
                  data-sel="best-list-button"
                  {...item.shopLink}
                  className={styles.shopButton}
                >
                  {'Check price'}
                </CartButtonLink>
              ) : (
                <div className={clsx(styles.shopButton, styles.shopButtonPlaceholder)} />
              )}
              <BestFeatures
                unfold={item.bottomLine === null}
                negatives={item.negatives}
                positives={item.positives}
                positiveClassName={styles.featuresPositive}
                negativeClassName={styles.featuresNegative}
                paneClassName={styles.featuresPane}
              />

              <div className={styles.title}>{item.bottomLine ? 'The Bottom Line.' : ''}</div>

              <div className={styles.bottomLine}>
                {item.bottomLine}
                {item.reviewButton ? (
                  <Linked
                    onClick={() => {
                      appEvent({
                        event: 'widget-interaction',
                        widgetType: 'Clearbuy Best List Topper',
                        label: item.reviewButton?.pLink.href ?? '',
                        url: item.reviewButton?.label ?? '',
                      })
                    }}
                    className={styles.reviewLink}
                    {...item.reviewButton.pLink}
                  >
                    {item.reviewButton.label}
                  </Linked>
                ) : null}
              </div>
            </React.Fragment>
          ))}

          <SplitterLine className={styles.splitterLast} />
        </div>
      </div>
    </div>
  )
}

export default BestList
